/**
 * Join Campaign Hook
 * Unified join functionality for all campaign types (prize pop, tournaments, etc.)
 * Config-driven: joinCampaign API resolves the correct request format via config.
 */

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useJoinCampaignMutation,
  campaignQueryKeys,
  resolveCampaignConfig
} from './useCampaignsQuery';

import type { CampaignTypes } from '../../types/campaign.types';
import { useSettings } from '../../../../store/app/appStore';

/**
 * Hook for joining any campaign type.
 * Config-driven: the joinCampaign API function handles request format
 * differences (query params vs body) based on config.subFetches.join.buildJoinInRequestData.
 */
export const useJoinCampaign = (campaignType?: CampaignTypes) => {
  const settings = useSettings();
  const queryClient = useQueryClient();
  const joinCampaignMutation = useJoinCampaignMutation();

  const resolvedType = (campaignType || settings.campaignOnly?.type) as CampaignTypes;
  const { config, host, sessionInfo } = resolveCampaignConfig(resolvedType);

  const joinCampaign = useCallback(async (campaignId: string) => {
    try {
      const result = await joinCampaignMutation.mutateAsync({
        config,
        host,
        sessionInfo,
        data: [campaignId],
      });

      await queryClient.invalidateQueries({
        queryKey: campaignQueryKeys.lists()
      });

      return result;
    } catch (error) {
      console.error(`Failed to join ${resolvedType}:`, error);
      throw error;
    }
  }, [config, host, sessionInfo, resolvedType, joinCampaignMutation, queryClient]);

  return {
    joinCampaign,
    isJoiningCampaign: joinCampaignMutation.isPending,
    joinCampaignError: joinCampaignMutation.error,
  };
};
